import store from '@/store';
import { resolveBranchId } from '@/utils/activeBranch';

// v4: lookups are keyed by tenant unless they genuinely vary by branch, so the key shape changed.
export const LOOKUP_CACHE_VERSION = 4;
export const LOOKUP_CACHE_FAMILY_PREFIX = 'fitnet:lookup-cache:';
export const LOOKUP_CACHE_PREFIX = `${LOOKUP_CACHE_FAMILY_PREFIX}v${LOOKUP_CACHE_VERSION}`;
export const DEFAULT_LOOKUP_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
export const FAILURE_COOLDOWN_MS = 30 * 1000;

const pendingRequests = new Map();

const getStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (err) {
    return null;
  }
};

const normalizeValue = (value) => {
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((normalized, key) => {
        if (value[key] !== undefined) {
          normalized[key] = normalizeValue(value[key]);
        }
        return normalized;
      }, {});
  }
  return value;
};

const stableSerialize = (value) => JSON.stringify(normalizeValue(value || {}));

const getTenant = () => {
  const state = store && store.state;
  const tenantSetting = state && state.settings && state.settings.tenantSetting;
  const tenantId = tenantSetting && (tenantSetting.id || tenantSetting.tenantId);
  const tenantFallback = tenantSetting && tenantSetting.orgName;
  return tenantId || tenantFallback || null;
};

// Only these vary with the active branch -- the backend filters rooms and sections by branch.
// Everything else is tenant-wide or global reference data, and keying it by branch stored
// one identical copy per branch, each able to go stale on its own.
const BRANCH_SCOPED_LOOKUPS = ['lookups:rooms', 'lookups:divisions'];

const isBranchScoped = (key) => BRANCH_SCOPED_LOOKUPS.some((name) => key.startsWith(name));

const getTenantScope = (key) => {
  const state = store && store.state;
  const tenant = getTenant();
  if (!tenant) return null;

  if (!key || !isBranchScoped(key)) return stableSerialize({ tenant });

  // The entries are cached in localStorage and the branch switcher reloads the page, so
  // keying on the home branch would serve one branch's rooms to another.
  const branchId = resolveBranchId(state && state.auth);
  return stableSerialize({ tenant, branch: branchId || 'default' });
};

export const buildLookupCacheKey = (lookupName, params = {}) => (
  `${lookupName}:${stableSerialize(params)}`
);

export const lookupCacheKeys = {
  currencies: (params = {}) => buildLookupCacheKey('lookups:currencies', params),
  // Use a sub-prefix so invalidating 'lookups:currencies' clears both list endpoints.
  allCurrencies: (params = {}) => buildLookupCacheKey('lookups:currencies:all', params),
  branches: (params = {}) => buildLookupCacheKey('lookups:branches', params),
  activities: (params = {}) => buildLookupCacheKey('lookups:activities', params),
  rooms: (params = {}) => buildLookupCacheKey('lookups:rooms', params),
  divisions: (params = {}) => buildLookupCacheKey('lookups:divisions', params),
  levels: (params = {}) => buildLookupCacheKey('lookups:levels', params),
  genders: (params = {}) => buildLookupCacheKey('lookups:genders', params),
  systemLanguages: (params = {}) => buildLookupCacheKey('lookups:systemLanguages', params),
  timezones: (params = {}) => buildLookupCacheKey('lookups:timezones', params),
  dateFormats: (params = {}) => buildLookupCacheKey('lookups:dateFormats', params),
  paymentMethods: (params = {}) => buildLookupCacheKey('payments:methods', params),
  statuses: (params = {}) => buildLookupCacheKey('statuses', params),
};

const getStorageKey = (key) => {
  const scope = getTenantScope(key);
  return scope ? `${LOOKUP_CACHE_PREFIX}:${scope}:${key}` : null;
};

const removeStorageEntry = (storage, storageKey) => {
  try {
    storage.removeItem(storageKey);
  } catch (err) {
    // Storage cleanup failures are non-fatal; the next fetch still works.
  }
};

const readStorageEntry = (storage, storageKey) => {
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (
      !entry
      || entry.version !== LOOKUP_CACHE_VERSION
      || !Object.prototype.hasOwnProperty.call(entry, 'data')
    ) {
      removeStorageEntry(storage, storageKey);
      return null;
    }
    return entry;
  } catch (err) {
    removeStorageEntry(storage, storageKey);
    return null;
  }
};

const writeStorageEntry = (storage, storageKey, entry) => {
  try {
    storage.setItem(storageKey, JSON.stringify(entry));
  } catch (err) {
    // Quota or private-mode failures must not break lookup rendering.
  }
};

const removeStorageEntriesMatching = (storage, prefix) => {
  if (!storage) return;
  for (let i = storage.length - 1; i >= 0; i -= 1) {
    const key = storage.key(i);
    if (key && key.startsWith(prefix)) removeStorageEntry(storage, key);
  }
};

const hasLookupsModule = () => Boolean(store && store.state && store.state.lookups);

const readMemoryEntry = (storageKey) => {
  if (!hasLookupsModule()) return null;
  return store.state.lookups.entries[storageKey] || null;
};

const writeMemoryEntry = (storageKey, entry) => {
  if (!hasLookupsModule()) return;
  store.commit('lookups/setLookupEntry', { storageKey, entry });
};

const removeMemoryEntry = (storageKey) => {
  if (!hasLookupsModule()) return;
  store.commit('lookups/removeLookupEntry', storageKey);
};

const clearAllMemoryEntries = () => {
  if (!hasLookupsModule()) return;
  store.commit('lookups/clearLookupEntries');
};

export const clearLookupCacheRequests = () => {
  pendingRequests.clear();
};

export const invalidateLookup = (key) => {
  const storageKey = getStorageKey(key);
  if (!storageKey) return;
  removeMemoryEntry(storageKey);
  const storage = getStorage();
  if (storage) removeStorageEntry(storage, storageKey);
};

// A cache key carries the branch it was read under, but a mutation is not branch-specific:
// renaming a branch has to clear the copy every other branch scope holds too, or the old name
// survives there for the rest of the TTL. Over-invalidating costs a refetch; under-invalidating
// shows stale data.
const matchesTenantAndLookup = (key, tenant, lookupNamePrefix) => {
  if (!key.startsWith(`${LOOKUP_CACHE_PREFIX}:{`)) return false;

  const rest = key.slice(LOOKUP_CACHE_PREFIX.length + 1);
  const scopeEnd = rest.indexOf('}');
  if (scopeEnd === -1) return false;

  try {
    const scope = JSON.parse(rest.slice(0, scopeEnd + 1));
    if (scope.tenant !== tenant) return false;
  } catch (err) {
    return false;
  }

  return rest.slice(scopeEnd + 2).startsWith(lookupNamePrefix);
};

export const invalidateLookupsMatching = (lookupNamePrefix) => {
  const tenant = getTenant();
  if (tenant === null) return;

  const storage = getStorage();
  if (storage) {
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i);
      if (key && matchesTenantAndLookup(key, tenant, lookupNamePrefix)) {
        removeStorageEntry(storage, key);
      }
    }
  }

  if (!hasLookupsModule()) return;
  Object.keys(store.state.lookups.entries).forEach((key) => {
    if (matchesTenantAndLookup(key, tenant, lookupNamePrefix)) removeMemoryEntry(key);
  });
};

export const clearAllLookups = () => {
  clearAllMemoryEntries();
  removeStorageEntriesMatching(getStorage(), LOOKUP_CACHE_FAMILY_PREFIX);
};

export const sweepLookupCache = () => {
  const storage = getStorage();
  if (!storage) return;
  const now = Date.now();
  for (let i = storage.length - 1; i >= 0; i -= 1) {
    const key = storage.key(i);
    if (key && key.startsWith(LOOKUP_CACHE_FAMILY_PREFIX)) {
      if (!key.startsWith(LOOKUP_CACHE_PREFIX)) {
        removeStorageEntry(storage, key);
      } else {
        try {
          const raw = storage.getItem(key);
          const entry = raw ? JSON.parse(raw) : null;
          if (!entry || entry.version !== LOOKUP_CACHE_VERSION || entry.expiresAt <= now) {
            removeStorageEntry(storage, key);
          }
        } catch (err) {
          removeStorageEntry(storage, key);
        }
      }
    }
  }
};

const invokeFetcher = (fetcher) => {
  try {
    return Promise.resolve(fetcher());
  } catch (err) {
    return Promise.reject(err);
  }
};

export const getOrFetchLookup = ({
  key,
  fetcher,
  ttlMs = DEFAULT_LOOKUP_CACHE_TTL_MS,
  forceRefresh = false,
}) => {
  const storageKey = getStorageKey(key);
  const storage = getStorage();

  let cachedEntry = storageKey ? readMemoryEntry(storageKey) : null;
  if (!cachedEntry && storage && storageKey) {
    cachedEntry = readStorageEntry(storage, storageKey);
    if (cachedEntry) writeMemoryEntry(storageKey, cachedEntry);
  }

  const now = Date.now();

  if (!forceRefresh && cachedEntry && cachedEntry.expiresAt > now) {
    return Promise.resolve(cachedEntry.data);
  }

  const pendingKey = storageKey || `unscoped:${key}`;
  if (pendingRequests.has(pendingKey)) {
    return pendingRequests.get(pendingKey);
  }

  const request = invokeFetcher(fetcher)
    .then((data) => {
      const entry = {
        data,
        cachedAt: now,
        expiresAt: now + ttlMs,
        version: LOOKUP_CACHE_VERSION,
      };
      if (storageKey) {
        writeMemoryEntry(storageKey, entry);
        if (storage) writeStorageEntry(storage, storageKey, entry);
      }
      return data;
    })
    .catch((err) => {
      if (cachedEntry) {
        // Failure cooldown: extend the cached entry's life briefly so a
        // flapping endpoint doesn't trigger a network call on every read.
        const cooled = { ...cachedEntry, expiresAt: now + FAILURE_COOLDOWN_MS };
        if (storageKey) {
          writeMemoryEntry(storageKey, cooled);
          if (storage) writeStorageEntry(storage, storageKey, cooled);
        }
        return cachedEntry.data;
      }
      throw err;
    })
    .finally(() => {
      pendingRequests.delete(pendingKey);
    });

  pendingRequests.set(pendingKey, request);
  return request;
};

// userId, not id: the login payload has no `id`, so reading that returned undefined for everyone
// and the "did the user change" test could never fire -- one operator's lookups, including their
// branch list, survived into the next operator's session on the same tenant.
const readUserId = (state) => {
  const auth = state && state.auth;
  return auth && auth.userData && auth.userData.userId;
};

const readTenantId = (state) => {
  const settings = state && state.settings;
  const tenant = settings && settings.tenantSetting;
  return tenant && (tenant.id || tenant.tenantId);
};

export const installLookupCache = (storeInstance) => {
  sweepLookupCache();

  let prevUserId = readUserId(storeInstance.state);
  let prevTenantId = readTenantId(storeInstance.state);

  storeInstance.subscribe((mutation, state) => {
    if (mutation.type !== 'changeUserData' && mutation.type !== 'changeTenantSettings') return;

    const userId = readUserId(state);
    const tenantId = readTenantId(state);

    const userChanged = prevUserId !== undefined && userId !== prevUserId;
    const tenantChanged = prevTenantId !== undefined && tenantId !== prevTenantId;
    prevUserId = userId;
    prevTenantId = tenantId;

    if (userChanged || tenantChanged) clearAllLookups();
  });

  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('storage', (event) => {
      // event.key === null means another tab cleared all of localStorage.
      if (event.key === null) {
        clearAllMemoryEntries();
        return;
      }
      if (!event.key.startsWith(LOOKUP_CACHE_FAMILY_PREFIX)) return;
      removeMemoryEntry(event.key);
    });
  }
};
